import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useConverter } from './hooks/useConverter';
import logo from './logo.png';
import './App.css';

function App() {
  const {
    convert,
    formatResult,
    getUnitsForCategory,
    getAllCategories
  } = useConverter();

  const [value, setValue] = useState('1');
  const [from, setFrom] = useState('km');
  const [to, setTo] = useState('m');
  const [result, setResult] = useState('');
  const [currentCategory, setCurrentCategory] = useState('length');
  const [conversionTriggered, setConversionTriggered] = useState(false);

  // Get units for current category (memoized)
  const units = useMemo(() => getUnitsForCategory(currentCategory), [currentCategory, getUnitsForCategory]);

  // Categories for preset buttons (memoized)
  const categories = useMemo(() => getAllCategories(), [getAllCategories]);

  // Perform conversion
  const performConversion = useCallback(() => {
    if (!value || isNaN(value)) {
      setResult('Invalid input');
      return;
    }

    try {
      const conversionResult = convert(
        parseFloat(value),
        from,
        to,
        currentCategory
      );

      const formattedResult = formatResult(conversionResult);
      setResult(formattedResult);
    } catch (error) {
      setResult('Conversion error');
    }
  }, [value, from, to, currentCategory, convert, formatResult]);

  // Handle input change
  const handleInputChange = (e) => {
    setValue(e.target.value);
    // Reset conversion trigger when input changes
    setConversionTriggered(false);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setCurrentCategory(category);

    // Reset units when changing category
    const categoryUnits = getUnitsForCategory(category);
    const unitKeys = Object.keys(categoryUnits);

    setFrom(unitKeys[0]);
    setTo(unitKeys[1] || unitKeys[0]);

    // Reset conversion trigger when category changes
    setConversionTriggered(false);
  };

  // Handle unit changes
  const handleFromUnitChange = (e) => {
    setFrom(e.target.value);
    // Reset conversion trigger when units change
    setConversionTriggered(false);
  };

  const handleToUnitChange = (e) => {
    setTo(e.target.value);
    // Reset conversion trigger when units change
    setConversionTriggered(false);
  };

  // Swap units
  const swapUnits = () => {
    setFrom(to);
    setTo(from);
    setConversionTriggered(false);
  };

  // Handle convert button click
  const handleConvert = () => {
    setConversionTriggered(true);
    performConversion();
  };

  // Perform conversion when conversion is triggered
  useEffect(() => {
    if (conversionTriggered) {
      performConversion();
    }
  }, [conversionTriggered, performConversion]);



  return (
    <div className="App">
      <div className="converter-card">
        <header>
          <h1 className="App-title">
            <img src={logo} className="App-logo" alt="Unit Converter Logo" />
            Unit Converter
          </h1>
          <p className="seo-description">
            Convert units instantly with our free online unit converter. Support for length, weight, temperature, volume, area, speed, time, and digital storage conversions. Fast, accurate, and easy to use.
          </p>
        </header>

        {/* Category Buttons */}
        <section aria-label="Conversion Categories">
          <h2 className="section-heading">Select Conversion Type</h2>
          <div className="category-buttons">
            {Object.entries(categories).map(([key, category]) => (
              <button
                key={key}
                className={`category-btn ${currentCategory === key ? 'active' : ''}`}
                onClick={() => handleCategoryChange(key)}
                aria-label={`Convert ${category.name} units`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </section>

        {/* Converter Tool */}
        <section aria-label="Unit Conversion Calculator">
          <h2 className="section-heading">Convert {categories[currentCategory]?.name} Units</h2>

          {/* Value to Convert */}
          <div className="input-group">
            <label className="input-label">Value to Convert</label>
            <input
              type="number"
              value={value}
              onChange={handleInputChange}
              placeholder="1"
              className="value-input-main"
              aria-label="Enter value to convert"
            />
          </div>

          {/* Convert From and To */}
          <div className="convert-row">
            <div className="convert-col">
              <label className="input-label">Convert From</label>
              <select
                value={from}
                onChange={handleFromUnitChange}
                className="unit-select"
              >
                {Object.keys(units).map(unit => (
                  <option key={unit} value={unit}>
                    {units[unit].fullName || unit}
                  </option>
                ))}
              </select>
            </div>

            <button className="swap-button" onClick={swapUnits} aria-label="Swap units">
              ⇄
            </button>

            <div className="convert-col">
              <label className="input-label">Convert To</label>
              <select
                value={to}
                onChange={handleToUnitChange}
                className="unit-select"
              >
                {Object.keys(units).map(unit => (
                  <option key={unit} value={unit}>
                    {units[unit].fullName || unit}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Convert Button */}
          <button className="convert-button" onClick={handleConvert}>
            CONVERT UNITS
          </button>

          {/* Result Display */}
          {result && (
            <div className="result-display" role="status" aria-live="polite">
              <div className="result-value">{result}</div>
              <div className="result-text">
                {value} {units[from]?.fullName || from} = {result} {units[to]?.fullName || to}
              </div>
            </div>
          )}
        </section>

        {/* SEO Content Section */}
        <section className="seo-content">
          <h2>About Our Unit Converter</h2>
          <p>
            Our free online unit converter helps you convert between different units of measurement quickly and accurately.
            Whether you need to convert length, weight, temperature, volume, area, speed, time, or digital storage,
            our tool provides instant results with high precision.
          </p>

          <h3>Supported Conversion Types</h3>
          <ul className="conversion-list">
            <li><strong>Length Converter:</strong> Convert between meters, kilometers, miles, feet, inches, and more</li>
            <li><strong>Weight Converter:</strong> Convert kilograms, grams, pounds, ounces, and other weight units</li>
            <li><strong>Temperature Converter:</strong> Convert Celsius, Fahrenheit, and Kelvin temperatures</li>
            <li><strong>Volume Converter:</strong> Convert liters, gallons, milliliters, and other volume measurements</li>
            <li><strong>Area Converter:</strong> Convert square meters, acres, hectares, and more</li>
            <li><strong>Speed Converter:</strong> Convert km/h, mph, m/s, and other speed units</li>
            <li><strong>Time Converter:</strong> Convert seconds, minutes, hours, days, and years</li>
            <li><strong>Data Storage Converter:</strong> Convert bytes, KB, MB, GB, TB, and more</li>
          </ul>

          <h3>Why Use Our Unit Converter?</h3>
          <ul className="features-list">
            <li>✓ Fast and accurate conversions</li>
            <li>✓ Support for metric and imperial systems</li>
            <li>✓ Easy-to-use interface</li>
            <li>✓ Mobile-friendly design</li>
            <li>✓ No registration required</li>
            <li>✓ Completely free to use</li>
          </ul>

          <h3>More Free Online Tools</h3>
          <p>
            Looking for more free calculators? Check out our{' '}
            <a
              href="https://age-calculator.onevista.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="external-link"
            >
              Age Calculator
            </a>
            {' '}to calculate your exact age in years, months, days, hours, minutes, and seconds instantly.
          </p>
        </section>
      </div>
    </div>
  );
}

export default React.memo(App);